import { UserData, UserSettings } from "@/lib/decks-utils/utils";
import { Folder, localDB } from "./db";
import { migrateCardFromLegacyToAnki } from "@/lib/spaced-repetition/migrations";
import { CardProps } from "@/lib/decks-utils/deck-types";

const createNewManualDeck = async ({
  deckName,
  displayName,
  color,
  imageBackground,
  thumbnailText,
  folder,
  cards = [],
}: {
  deckName: string;
  displayName: string;
  color?: string;
  imageBackground?: string;
  thumbnailText?: string;
  folder: string;
  cards?: CardProps[];
}) => {
  await localDB.deckProperties.put({
    name: deckName,
    displayName: displayName,
    color: color,
    imageBackground: imageBackground,
    thumbnailText: thumbnailText,
    folder: folder,
  });

  await localDB.updateDeckCards(deckName, cards);
};

const syncLocalWithRemote = async ({
  remoteDecks,
  deckNames,
  remoteAiDecks,
  remoteData,
  localFolders,
  remoteFolders,
  remoteSettings,
}: {
  remoteDecks: Record<string, CardProps[]>;
  deckNames: string[];
  remoteAiDecks: string[];
  remoteData: UserData;
  localFolders: Folder[];
  remoteFolders: Folder[];
  remoteSettings: UserSettings;
}) => {
  try {
    // Start a transaction for atomic operations
    await localDB.transaction(
      "rw",
      [
        localDB.decks,
        localDB.folders,
        localDB.deckProperties,
        localDB.settings,
      ],
      async () => {
        // NOTE: BE CAREFUL TO NOT TRIGGER ANY FUNCTION HERE THAT UPDATES THE TIMESTAMP
        // Because this will trigger a lot of syncing!

        for (const [deckName, remoteCards] of Object.entries(remoteDecks)) {
          const localDeck = await localDB.decks.get(deckName);
          const localCards = localDeck?.cards || [];

          let cardsToKeep: CardProps[] = [];
          let cardsToUpdate: Record<string, CardProps> = {};
          // Currently it's impossible to delete a flashcard - we consider the one with
          // the most flashcards to be the main source of truth
          if (remoteCards.length >= localCards.length) {
            cardsToKeep = remoteCards;
            // Create a map of local cards with their IDs as keys
            cardsToUpdate = localCards.reduce((acc, card) => {
              if (!card.id) {
                return acc;
              }
              acc[card.id] = card;
              return acc;
            }, {} as Record<string, CardProps>);
          } else {
            cardsToKeep = localCards;
            cardsToUpdate = remoteCards.reduce((acc, card) => {
              if (!card.id) {
                return acc;
              }
              acc[card.id] = card;
              return acc;
            }, {} as Record<string, CardProps>);
          }

          localDB.decks.put(
            {
              name: deckName,
              cards: cardsToKeep
                .map((cardToKeep) => {
                  const cardToUpdate = cardsToUpdate[cardToKeep.id];

                  // Double-check that the created-time is the same, if not they might
                  // not be the same card!
                  if (
                    cardToKeep.createdTimeISO !== cardToUpdate?.createdTimeISO
                  ) {
                    return cardToKeep;
                  }

                  // If a card is deleted, we return that one
                  if (
                    cardToUpdate.deletedTimeISO ||
                    cardToKeep.deletedTimeISO
                  ) {
                    if (cardToKeep.deletedTimeISO) {
                      return cardToKeep;
                    }
                    if (cardToUpdate.deletedTimeISO) {
                      return cardToUpdate;
                    }
                  }

                  if (cardToUpdate) {
                    if (
                      cardToUpdate.studyState.length >
                      cardToKeep.studyState.length
                    ) {
                      return cardToUpdate;
                    }
                  }
                  return cardToKeep;
                })
                .map(migrateCardFromLegacyToAnki),
            },
            deckName
          );
        }

        const aiDecksSet = new Set(deckNames);
        const aiDecksSyncSet = new Set(remoteAiDecks);

        // Delete the decks that have been deleted remotely but not locally
        const decksNotInLocal = [...aiDecksSet].filter(
          (x) => !aiDecksSyncSet.has(x)
        );
        for (const deckName of decksNotInLocal) {
          localDB.decks.delete(deckName);
        }

        const remoteFoldersSet = new Set(
          (remoteFolders || []).map((folder) => folder.id)
        );
        const foldersNotInRemote = [...localFolders].filter(
          (x) => !remoteFoldersSet.has(x.id)
        );
        for (const folderId of foldersNotInRemote) {
          localDB.folders.delete(folderId.id);
        }

        for (const [folderId, folder] of Object.entries(
          remoteData.data.decks.folders
        )) {
          localDB.folders.put(
            {
              ...folder,
            },
            folderId
          );
        }

        for (const [deckName, properties] of Object.entries(
          remoteData.data.decks.additionalProperites
        )) {
          localDB.deckProperties.put({
            name: deckName,
            displayName: properties.displayName,
            imageBackground:
              properties.imageBackground || properties.imageBackround,
            color: properties.color,
            thumbnailText: properties.thumbnailText,
            languageSettings: properties.languageSettings,
            folder: properties.folder,
          });
        }

        if (remoteSettings?.spacedRepetition) {
          localDB.settings.put({
            key: "globalSrSettings",
            value: remoteSettings.spacedRepetition,
          });
        }

        await localDB.setLatestUpdateTimestamp(remoteData.data.updated_at);
      }
    );
  } catch (error) {
    console.error("Error during sync:", error);
    throw new Error("Failed to synchronize with remote data");
  }
};

export const localDBUtils = { createNewManualDeck, syncLocalWithRemote };
